import { useEffect, useMemo, useRef } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useQuery } from "@apollo/client";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import MenuDropdown from "@/components/MenuDropdown";
import Select from "@/components/Select";
import { WorkingLocation } from "@/types";
import SingleDatePicker from "@/pages/people/[pgaId]/person/_components/SingleDatePicker";
import { FacilityAutocomplete } from "@/pages/people/[pgaId]/person/_components/FacilityAutocomplete";
import { EMPLOYMENT_CLASSIFICATIONS_QUERY, EMPLOYMENT_TITLES_QUERY, TOUR_CODES_QUERY } from "./employmentTitlesQueries";

type FieldValues = {
  jobClassification: string;
  jobTitle: string;
  tourCode: string;
  facilityId: string;
  workingLocation: WorkingLocation | "";
  startDate: string;
};

type ClassificationOption = {
  id: string;
  name: string;
};

type JobTitleOption = {
  id: string;
  jobTitle: string;
  jobClassification: string;
};

export type UpdateEmploymentClassificationAndTitleValues = {
  jobClassification: string;
  jobTitle: string;
  tourCode?: string | null;
  facilityId?: string | null;
  workingLocation?: WorkingLocation | null;
  startDate?: string | null;
};

const workingLocationOptions = [
  { label: "Select working location", value: "" },
  { label: "On-site", value: WorkingLocation.ON_SITE },
  { label: "Remote", value: WorkingLocation.REMOTE }
];

interface Props {
  open: boolean;
  onClose: () => void;
  programType: "associate" | "member";
  employer?: string | null;
  currentFacilityId?: string | null;
  currentJobTitle?: string | null;
  currentJobClassification?: string | null;
  currentTourCode?: string | null;
  currentWorkingLocation?: WorkingLocation | null;
  currentStartDate?: string | null;
  saving?: boolean;
  onSave: (values: UpdateEmploymentClassificationAndTitleValues) => void | Promise<void>;
}

export default function UpdateEmploymentClassificationAndTitleModal({
  open,
  onClose,
  programType,
  employer,
  currentFacilityId,
  currentJobTitle,
  currentJobClassification,
  currentTourCode,
  currentWorkingLocation,
  currentStartDate,
  saving = false,
  onSave
}: Props) {
  const isAssociateApplication = programType === "associate";

  const { data: classificationData, loading: classificationsLoading } = useQuery(EMPLOYMENT_CLASSIFICATIONS_QUERY, {
    skip: !open,
    variables: { programType }
  });

  const classificationOptions = useMemo<ClassificationOption[]>(
    () => classificationData?.employmentClassifications ?? [],
    [classificationData]
  );

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isSubmitting }
  } = useForm<FieldValues>({
    defaultValues: {
      jobClassification: "",
      jobTitle: "",
      tourCode: "",
      facilityId: "",
      workingLocation: "",
      startDate: ""
    },
    mode: "onChange"
  });

  const selectedClassification = useWatch({ control, name: "jobClassification" });
  const selectedJobTitle = useWatch({ control, name: "jobTitle" });
  const selectedFacilityId = useWatch({ control, name: "facilityId" });
  const selectedWorkingLocation = useWatch({ control, name: "workingLocation" });
  const selectedStartDate = useWatch({ control, name: "startDate" });
  const previousClassificationRef = useRef<string | null>(null);

  const { data: titlesData, loading: titlesLoading } = useQuery(EMPLOYMENT_TITLES_QUERY, {
    skip: !open || !selectedClassification,
    variables: {
      programType,
      jobClassification: selectedClassification || null
    }
  });

  const jobTitleOptions = useMemo<JobTitleOption[]>(() => titlesData?.employmentTitles ?? [], [titlesData]);

  const needsTourCode = programType === "member" && selectedClassification === "A-3";
  const needsReplacementFacility =
    programType === "member" && currentJobClassification === "A-3" && selectedClassification !== "A-3";
  const showAssociateFacilityField = isAssociateApplication;

  const { data: tourCodesData, loading: tourCodesLoading } = useQuery(TOUR_CODES_QUERY, {
    skip: !open || !needsTourCode
  });

  const currentTourOptions = useMemo(
    () => (tourCodesData?.tourCodes ?? []).filter((tour) => tour.current),
    [tourCodesData]
  );

  useEffect(() => {
    if (!open) {
      previousClassificationRef.current = null;
      return;
    }

    if (isAssociateApplication) {
      reset({
        jobClassification: currentJobClassification ?? "",
        jobTitle: currentJobTitle ?? "",
        tourCode: "",
        facilityId: currentFacilityId ?? "",
        workingLocation: currentWorkingLocation ?? "",
        startDate: currentStartDate ?? ""
      });
    } else {
      reset({
        jobClassification: currentJobClassification ?? "",
        jobTitle: "",
        tourCode: currentTourCode ?? "",
        facilityId: "",
        workingLocation: "",
        startDate: ""
      });
    }
    previousClassificationRef.current = currentJobClassification ?? "";
  }, [
    open,
    isAssociateApplication,
    currentJobClassification,
    currentJobTitle,
    currentTourCode,
    currentFacilityId,
    currentWorkingLocation,
    currentStartDate,
    reset
  ]);

  useEffect(() => {
    if (!open || !selectedClassification) return;

    if (previousClassificationRef.current !== null && previousClassificationRef.current !== selectedClassification) {
      setValue("jobTitle", "", { shouldValidate: true });
      setValue("tourCode", "", { shouldValidate: true });
      if (!isAssociateApplication) {
        setValue("facilityId", "", { shouldValidate: true });
      }
    }
    previousClassificationRef.current = selectedClassification;
  }, [open, selectedClassification, setValue, isAssociateApplication]);

  useEffect(() => {
    if (!open || !selectedClassification || jobTitleOptions.length === 0) return;
    if (selectedJobTitle) return;

    const matchingTitle = jobTitleOptions.find((option) => option.jobTitle === currentJobTitle);
    if (matchingTitle && selectedClassification === (currentJobClassification ?? "")) {
      setValue("jobTitle", matchingTitle.jobTitle, { shouldValidate: true });
    }
  }, [
    open,
    selectedClassification,
    selectedJobTitle,
    jobTitleOptions,
    currentJobTitle,
    currentJobClassification,
    setValue
  ]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (values: FieldValues) => {
    await onSave({
      jobClassification: values.jobClassification,
      jobTitle: values.jobTitle,
      tourCode: needsTourCode ? values.tourCode || null : null,
      facilityId: isAssociateApplication
        ? values.facilityId || null
        : needsReplacementFacility
          ? values.facilityId || null
          : null,
      workingLocation: isAssociateApplication ? values.workingLocation || null : null,
      startDate: isAssociateApplication ? values.startDate || null : null
    });
  };

  const isSaving = saving || isSubmitting;
  const titlesReady = !selectedClassification || !titlesLoading;
  const tourCodeReady = !needsTourCode || !tourCodesLoading;
  // Associate: preserve null facility/start date when unchanged; block Save only if the reviewer clears an existing value.
  const hadFacility = Boolean(currentFacilityId?.trim());
  const hadStartDate = Boolean(currentStartDate?.trim());
  const facilityReady =
    (!showAssociateFacilityField && !needsReplacementFacility) ||
    Boolean(selectedFacilityId?.trim()) ||
    (showAssociateFacilityField && !hadFacility);
  const workingLocationReady = !isAssociateApplication || Boolean(selectedWorkingLocation);
  const startDateReady = !isAssociateApplication || Boolean(selectedStartDate?.trim()) || !hadStartDate;

  return (
    <Dialog
      className="p-6 pr-2 max-h-[80vh] w-[648px] max-w-[90vw]"
      open={open}
      onClosed={handleClose}
      headline="Update Employment"
      hasFormElements
      displayOverflow
      actions={
        <div className="flex gap-2">
          <Button variant="text" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            variant="filled"
            onClick={handleSubmit(onSubmit)}
            disabled={
              !isValid ||
              isSaving ||
              classificationsLoading ||
              !titlesReady ||
              !tourCodeReady ||
              !facilityReady ||
              !workingLocationReady ||
              !startDateReady
            }
            testId="update-employment-classification-save"
          >
            Save
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        {(employer || currentJobTitle) && (
          <p className="text-body-medium text-sys-brand-on-surface-variant">
            {[employer, currentJobTitle].filter(Boolean).join(" · ")}
          </p>
        )}
        {showAssociateFacilityField && (
          <FacilityAutocomplete
            control={control}
            name="facilityId"
            label={hadFacility ? "Search Facility *" : "Search Facility"}
            initialDisplayName={employer ?? undefined}
            required={hadFacility}
            excludeArchived
          />
        )}
        <Controller
          name="jobClassification"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <MenuDropdown
              label="Classification*"
              options={classificationOptions}
              value={classificationOptions.find((option) => option.name === value)}
              onSelect={(option) => onChange(option.name)}
              getLabel={(option) => option.name}
              renderItem={(option) => (
                <div className="flex items-start gap-3 w-full" data-testid={`classification-option-${option.name}`}>
                  <div className="text-body-large">{option.name}</div>
                </div>
              )}
            />
          )}
        />
        <Controller
          name="jobTitle"
          control={control}
          rules={{ required: true }}
          render={({ field: { onChange, value } }) => (
            <MenuDropdown
              label="Job Title*"
              options={jobTitleOptions}
              value={jobTitleOptions.find((option) => option.jobTitle === value)}
              onSelect={(option) => onChange(option.jobTitle)}
              getLabel={(option) => option.jobTitle}
              disabled={!selectedClassification || titlesLoading}
              renderItem={(option) => (
                <div className="flex items-start gap-3 w-full" data-testid={`job-title-option-${option.id}`}>
                  <div className="text-body-large">{option.jobTitle}</div>
                </div>
              )}
            />
          )}
        />
        {needsReplacementFacility && <FacilityAutocomplete control={control} name="facilityId" label="Facility *" />}
        {needsTourCode && (
          <Controller
            name="tourCode"
            control={control}
            rules={{ required: "Tour code is required for A-3 classification" }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <Select
                label="Tour Code *"
                options={currentTourOptions.map((tour) => ({
                  label: tour.name,
                  value: tour.code
                }))}
                value={value}
                onSelect={onChange}
                error={!!error}
                errorText={error?.message}
                data-testid="tour-code-select"
              />
            )}
          />
        )}
        {isAssociateApplication && (
          <>
            <Controller
              name="workingLocation"
              control={control}
              rules={{ required: "Working location is required" }}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Select
                  label="Working Location"
                  options={workingLocationOptions}
                  value={value}
                  onSelect={onChange}
                  required
                  error={!!error}
                  errorText={error?.message}
                />
              )}
            />
            <SingleDatePicker control={control} label="Start Date" name="startDate" required={hadStartDate} useModal />
          </>
        )}
      </div>
    </Dialog>
  );
}
